import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import axios from "axios";

// ─── Active project filter interceptor ──────────────────────────────────
// Installed at module load (before any component renders) so the very first
// listing requests pick up the filter. Strips query params before matching.
(function installProjectFilter() {
  const FILTERED = [
    "/api/actions",
    "/api/fiches-action",
    "/api/rencontres",
    "/api/taches",
    "/api/cartographies",
  ];
  axios.interceptors.request.use((config) => {
    try {
      if ((config.method || "get").toLowerCase() !== "get") return config;
      const raw = (config.url || "").split("?")[0];
      if (!FILTERED.some((p) => raw.endsWith(p))) return config;
      const pid = localStorage.getItem("active_project_id");
      if (!pid || pid === "undefined" || pid === "null") return config;
      config.params = { ...(config.params || {}), project_id: pid };
    } catch { /* ignore */ }
    return config;
  });
})();
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// ─── Dark mode bootstrap (avoid FOUC) ──────────────────────────────────
(function bootstrapDarkMode() {
  try {
    // Thème clair par défaut : le sombre n'est activé que sur choix explicite (Sécurité › Apparence),
    // jamais d'après la préférence système (textes illisibles sur fonds teintés).
    if (localStorage.getItem("ui:theme") === "dark") document.documentElement.classList.add("dark");
  } catch { /* ignore */ }
})();
import LoginPage from "@/pages/LoginPage";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const ActionDetailPage = lazy(() => import("@/pages/ActionDetailPage"));
const ActionFormPage = lazy(() => import("@/pages/ActionFormPage"));
const StatsPage = lazy(() => import("@/pages/StatsPage"));
const BudgetPage = lazy(() => import("@/pages/BudgetPage"));
const SenacsPage = lazy(() => import("@/pages/SenacsPage"));
const GlobalProjectsPage = lazy(() => import("@/pages/GlobalProjectsPage"));
const GlobalProjectDetailPage = lazy(() => import("@/pages/GlobalProjectDetailPage"));
const ObjectivesPage = lazy(() => import("@/pages/ObjectivesPage"));
const ObjectiveDetailPage = lazy(() => import("@/pages/ObjectiveDetailPage"));
const RencontresPage = lazy(() => import("@/pages/RencontresPage"));
const RencontreDetailPage = lazy(() => import("@/pages/RencontreDetailPage"));
const RencontreFormPage = lazy(() => import("@/pages/RencontreFormPage"));
const BilanBesoinsPage = lazy(() => import("@/pages/BilanBesoinsPage"));
const TachesPage = lazy(() => import("@/pages/TachesPage"));
const MyResponsibilitiesPage = lazy(() => import("@/pages/MyResponsibilitiesPage"));
const TeamRaciPage = lazy(() => import("@/pages/TeamRaciPage"));
const TacheFormPage = lazy(() => import("@/pages/TacheFormPage"));
const CartographiesPage = lazy(() => import("@/pages/CartographiesPage"));
const CartographieFormPage = lazy(() => import("@/pages/CartographieFormPage"));
const CartographieDetailPage = lazy(() => import("@/pages/CartographieDetailPage"));
const FichesActionPage = lazy(() => import("@/pages/FichesActionPage"));
const FicheActionFormPage = lazy(() => import("@/pages/FicheActionFormPage"));
const FicheActionDetailPage = lazy(() => import("@/pages/FicheActionDetailPage"));
const ArchivesPage = lazy(() => import("@/pages/ArchivesPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const ActivityLogPage = lazy(() => import("@/pages/ActivityLogPage"));
const BilanSemestrielPage = lazy(() => import("@/pages/BilanSemestrielPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const PersonnesPage = lazy(() => import("@/pages/PersonnesPage"));
const PersonneFormPage = lazy(() => import("@/pages/PersonneFormPage"));
const PersonneDetailPage = lazy(() => import("@/pages/PersonneDetailPage"));
const PersonnesDashboardPage = lazy(() => import("@/pages/PersonnesDashboardPage"));
const CentersPage = lazy(() => import("@/pages/CentersPage"));
const CentersComparativePage = lazy(() => import("@/pages/CentersComparativePage"));
const AuditLogPage = lazy(() => import("@/pages/AuditLogPage"));
const DemoRequestsPage = lazy(() => import("@/pages/DemoRequestsPage"));
const GlobalUsersPage = lazy(() => import("@/pages/GlobalUsersPage"));
const NetworkHealthPage = lazy(() => import("@/pages/NetworkHealthPage"));
const MultiCenterDashboardPage = lazy(() => import("@/pages/MultiCenterDashboardPage"));
const InternalDocsPage = lazy(() => import("@/pages/InternalDocsPage"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const PasswordResetsPage = lazy(() => import("@/pages/PasswordResetsPage"));
const MyActivityPage = lazy(() => import("@/pages/MyActivityPage"));
const TagsManagementPage = lazy(() => import("@/pages/TagsManagementPage"));
const PlansPage = lazy(() => import("@/pages/PlansPage"));
const SupportTicketsPage = lazy(() => import("@/pages/SupportTicketsPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const BillingSuccessPage = lazy(() => import("@/pages/BillingPage").then(mod => ({ default: mod.BillingSuccessPage })));
const BillingCancelPage = lazy(() => import("@/pages/BillingPage").then(mod => ({ default: mod.BillingCancelPage })));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const FederationsPage = lazy(() => import("@/pages/FederationsPage"));
const SecteursPage = lazy(() => import("@/pages/SecteursPage"));
const BlogListPage = lazy(() => import("@/pages/BlogPages").then(mod => ({ default: mod.BlogListPage })));
const BlogPostPage = lazy(() => import("@/pages/BlogPages").then(mod => ({ default: mod.BlogPostPage })));
const BlogAdminPage = lazy(() => import("@/pages/BlogAdminPage"));
const TagSummaryPage = lazy(() => import("@/pages/TagSummaryPage"));
const ContactsPage = lazy(() => import("@/pages/ContactsPage"));
const BilanTemplatesPage = lazy(() => import("@/pages/BilanTemplatesPage"));
const AgendaPage = lazy(() => import("@/pages/AgendaPage"));
const PrivacyPage = lazy(() => import("@/pages/LegalPages").then(mod => ({ default: mod.PrivacyPage })));
const LegalNoticePage = lazy(() => import("@/pages/LegalPages").then(mod => ({ default: mod.LegalNoticePage })));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const UserGuidePage = lazy(() => import("@/pages/UserGuidePage"));
const MyVolunteerHoursPage = lazy(() => import("@/pages/MyVolunteerHoursPage"));
const BenevolesAdminPage = lazy(() => import("@/pages/BenevolesAdminPage"));
const PublicFeedbackPage = lazy(() => import("@/pages/PublicFeedbackPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const ProjectFormPage = lazy(() => import("@/pages/ProjectFormPage"));
const ProjectDetailPage = lazy(() => import("@/pages/ProjectDetailPage"));
import { ProjectProvider } from "@/contexts/ProjectContext";
const ShowcasePage = lazy(() => import("@/pages/ShowcasePage"));
const PitchPage = lazy(() => import("@/pages/PitchPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const SiteContentAdminPage = lazy(() => import("@/pages/SiteContentAdminPage"));
const ConversionsDashboardPage = lazy(() => import("@/pages/ConversionsDashboardPage"));
import OfflineIndicator from "@/components/OfflineIndicator";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import CommandPalette from "@/components/CommandPalette";
import ErrorBoundary from "@/components/ErrorBoundary";
import useIdleLogout from "@/hooks/useIdleLogout";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider } from "@/contexts/SettingsContext";

// Loading fallback while a lazy page chunk is being fetched
const PageFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center" data-testid="page-loading">
    <div className="h-8 w-8 rounded-full border-2 border-teal-600 border-t-transparent animate-spin" aria-label="Chargement" />
  </div>
);

// Public wrapper (inner) — used within a shared BrowserRouter/Routes tree so
// that <Link> navigation between public pages actually re-renders the tree.
const PublicWrapInner = ({ children }) => (
  <>
    {children}
    <Toaster />
  </>
);

// Legacy PublicWrap kept for backwards-compat with older imports if any.
const PublicWrap = ({ children }) => (
  <>
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </BrowserRouter>
    <Toaster />
  </>
);


// Auth context
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Setup axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

function RoleAwareHome() {
  const auth = useAuth();
  if (auth?.isSuperAdmin) {
    return <CentersComparativePage />;
  }
  // Admin or coordinateur managing multiple centers gets the consolidated dashboard by default
  const managedCount = auth?.user?.managed_center_ids?.length || 0;
  if ((auth?.isAdmin || auth?.isCoordinateur) && managedCount > 1) {
    return <MultiCenterDashboardPage />;
  }
  return <DashboardPage />;
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [user, setUser] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("auth_user"));
      // Defensive: sanity-check the stored user shape. If the payload is
      // corrupted or from an older incompatible version, treat it as absent
      // so the app falls back to a clean re-login flow instead of crashing
      // on missing fields.
      if (raw && typeof raw === "object" && raw.email && raw.role) {
        return raw;
      }
      return null;
    } catch {
      return null;
    }
  });

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const isCoordinateur = user?.role === "coordinateur";

  // Refresh features from /auth/me on app load so admin changes propagate without re-login
  React.useEffect(() => {
    if (token) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`)
        .then(({ data }) => {
          // Defensive merge: if the server no longer recognizes the session,
          // or returns an empty payload, force a clean logout rather than
          // rendering a page with inconsistent user data.
          if (!data || !data.email || !data.role) {
            handleLogout();
            return;
          }
          const merged = { ...user, ...data };
          setUser(merged);
          localStorage.setItem("auth_user", JSON.stringify(merged));
        })
        .catch((err) => {
          // Unknown error while refreshing session — don't crash the UI.
          // If the token is flat-out invalid (401), the axios response
          // interceptor above already handles the logout + reload.
          // eslint-disable-next-line no-console
          console.warn("auth/me refresh failed:", err?.message);
        });
    }
    // eslint-disable-next-line
  }, [token]);

  const handleLogin = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    // If we're still on the /login URL after authenticating, go home so the router finds a matching route.
    try {
      if (window.location.pathname === "/login") {
        window.history.replaceState({}, "", "/");
      }
    } catch { /* ignore */ }
  };

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    setToken(null);
    setUser(null);
  };

  // Auto-logout after 30 minutes of inactivity (warns 1 min before)
  useIdleLogout(!!token, handleLogout, 30 * 60 * 1000, 60 * 1000);

  // Activate offline auto-sync once at startup (no-op if already installed)
  useEffect(() => {
    import("@/utils/offlineQueue").then((m) => m.installAutoSync()).catch(() => {});
  }, []);

  if (!token) {
    // Public pages accessible without authentication.
    // We route them via a proper <Routes> tree so that <Link> navigation
    // between them (e.g. clicking "Blog" from the pricing page) actually
    // remounts the target component instead of just updating the URL.
    return (
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/confidentialite" element={<PublicWrapInner><PrivacyPage /></PublicWrapInner>} />
              <Route path="/mentions-legales" element={<PublicWrapInner><LegalNoticePage /></PublicWrapInner>} />
              <Route path="/a-propos" element={<PublicWrapInner><AboutPage /></PublicWrapInner>} />
              <Route path="/tarifs" element={<PublicWrapInner><PricingPage /></PublicWrapInner>} />
              <Route path="/fonctionnalites" element={<PublicWrapInner><FeaturesPage /></PublicWrapInner>} />
              <Route path="/federations" element={<PublicWrapInner><FederationsPage /></PublicWrapInner>} />
              <Route path="/secteurs" element={<PublicWrapInner><SecteursPage /></PublicWrapInner>} />
              <Route path="/blog" element={<PublicWrapInner><BlogListPage /></PublicWrapInner>} />
              <Route path="/blog/:slug" element={<PublicWrapInner><BlogPostPage /></PublicWrapInner>} />
              <Route path="/showcase/:slug" element={<PublicWrapInner><ShowcasePage /></PublicWrapInner>} />
              <Route path="/pitch" element={<PublicWrapInner><PitchPage /></PublicWrapInner>} />
              <Route path="/f/:token" element={<PublicWrapInner><PublicFeedbackPage /></PublicWrapInner>} />
              <Route
                path="*"
                element={
                  <>
                    <LoginPage onLogin={handleLogin} />
                    <Toaster />
                  </>
                }
              />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAdmin, isSuperAdmin, isCoordinateur, logout: handleLogout }}>
      <SettingsProvider>
        <ProjectProvider user={user}>
        <div className="App">
          <a
            href="#main-content"
            className="skip-to-content"
            data-testid="skip-to-content"
          >
            Aller au contenu principal
          </a>
          <ImpersonationBanner user={user} />
          <BrowserRouter>
            <ErrorBoundary>
            <CommandPalette />
            <main id="main-content" className="pb-28 lg:pb-0" tabIndex={-1}>
              <Suspense fallback={<PageFallback />}>
              <Routes>
              <Route path="/" element={<RoleAwareHome />} />
              <Route path="/multi-centres" element={<MultiCenterDashboardPage />} />
              <Route path="/dashboard-centre" element={<DashboardPage />} />
              <Route path="/actions" element={<HomePage />} />
              <Route path="/actions/new" element={<ActionFormPage />} />
              <Route path="/actions/:id" element={<ActionDetailPage />} />
              <Route path="/actions/:id/edit" element={<ActionFormPage />} />
              <Route path="/calendar" element={<AgendaPage />} />
              <Route path="/stats" element={<StatsPage />} />
              <Route path="/budget" element={<BudgetPage />} />
              <Route path="/senacs" element={<SenacsPage />} />
              <Route path="/projets-globaux" element={<GlobalProjectsPage />} />
              <Route path="/projets-globaux/:id" element={<GlobalProjectDetailPage />} />
              <Route path="/objectifs" element={<ObjectivesPage />} />
              <Route path="/objectifs/:id" element={<ObjectiveDetailPage />} />
              <Route path="/rencontres" element={<RencontresPage />} />
              <Route path="/rencontres/new" element={<RencontreFormPage />} />
              <Route path="/rencontres/bilan" element={<BilanBesoinsPage />} />
              <Route path="/rencontres/:id" element={<RencontreDetailPage />} />
              <Route path="/rencontres/:id/edit" element={<RencontreFormPage />} />
              <Route path="/taches" element={<TachesPage />} />
              <Route path="/taches/new" element={<TacheFormPage />} />
              <Route path="/taches/:id/edit" element={<TacheFormPage />} />
              <Route path="/mes-taches" element={<TachesPage />} />
              <Route path="/mes-responsabilites" element={<MyResponsibilitiesPage />} />
              <Route path="/qui-fait-quoi" element={<TeamRaciPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/agenda" element={<AgendaPage />} />
              {isAdmin && <Route path="/etiquettes" element={<TagsManagementPage />} />}
              {isAdmin && <Route path="/parametres/bilans" element={<BilanTemplatesPage />} />}
              <Route path="/etiquettes/:tagId/bilan" element={<TagSummaryPage />} />
              <Route path="/cartographies" element={<CartographiesPage />} />
              <Route path="/cartographies/new" element={<CartographieFormPage />} />
              <Route path="/cartographies/:id" element={<CartographieDetailPage />} />
              <Route path="/cartographies/:id/edit" element={<CartographieFormPage />} />
              <Route path="/fiches-action" element={<FichesActionPage />} />
              <Route path="/fiches-action/new" element={<FicheActionFormPage />} />
              <Route path="/fiches-action/:id" element={<FicheActionDetailPage />} />
              <Route path="/fiches-action/:id/edit" element={<FicheActionFormPage />} />
              <Route path="/personnes" element={<PersonnesPage />} />
              <Route path="/personnes/dashboard" element={<PersonnesDashboardPage />} />
              <Route path="/personnes/new" element={<PersonneFormPage />} />
              <Route path="/personnes/:id" element={<PersonneDetailPage />} />
              <Route path="/personnes/:id/edit" element={<PersonneFormPage />} />
              <Route path="/archives" element={<ArchivesPage />} />
              <Route path="/bilan" element={<BilanSemestrielPage />} />
              {isAdmin && <Route path="/utilisateurs" element={<UsersPage />} />}
              {isAdmin && <Route path="/journal" element={<ActivityLogPage />} />}
              {isAdmin && <Route path="/parametres" element={<SettingsPage />} />}
              {isSuperAdmin && <Route path="/centres" element={<CentersPage />} />}
              {isSuperAdmin && <Route path="/plans" element={<PlansPage />} />}
              {isSuperAdmin && <Route path="/admin/blog" element={<BlogAdminPage />} />}
              {isSuperAdmin && <Route path="/admin/site" element={<SiteContentAdminPage />} />}
              {isSuperAdmin && <Route path="/admin/conversions" element={<ConversionsDashboardPage />} />}
              <Route path="/tarifs" element={<PricingPage />} />
              <Route path="/fonctionnalites" element={<FeaturesPage />} />
              <Route path="/federations" element={<FederationsPage />} />
              <Route path="/secteurs" element={<SecteursPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/support" element={<SupportTicketsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/billing/success" element={<BillingSuccessPage />} />
              <Route path="/billing/cancel" element={<BillingCancelPage />} />
              {isSuperAdmin && <Route path="/centres/comparatif" element={<CentersComparativePage />} />}
              {isSuperAdmin && <Route path="/audit" element={<AuditLogPage />} />}
              {isSuperAdmin && <Route path="/demo-requests" element={<DemoRequestsPage />} />}
              {isSuperAdmin && <Route path="/utilisateurs-reseau" element={<GlobalUsersPage />} />}
              {isSuperAdmin && <Route path="/sante-reseau" element={<NetworkHealthPage />} />}
              {isSuperAdmin && <Route path="/documentation" element={<InternalDocsPage />} />}
              <Route path="/securite" element={<SecurityPage />} />
              {isSuperAdmin && <Route path="/demandes-reset" element={<PasswordResetsPage />} />}
              <Route path="/mon-activite" element={<MyActivityPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />
              <Route path="/mentions-legales" element={<LegalNoticePage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/ressources" element={<ResourcesPage />} />
              <Route path="/ressources/guide" element={<UserGuidePage />} />
              <Route path="/mes-heures" element={<MyVolunteerHoursPage />} />
              <Route path="/benevoles" element={<BenevolesAdminPage />} />
              <Route path="/projets" element={<ProjectsPage />} />
              <Route path="/projets/new" element={<ProjectFormPage />} />
              <Route path="/projets/:id" element={<ProjectDetailPage />} />
              <Route path="/projets/:id/edit" element={<ProjectFormPage />} />
              <Route path="/showcase/:slug" element={<ShowcasePage />} />
              <Route path="/pitch" element={<PitchPage />} />
              <Route path="/f/:token" element={<PublicFeedbackPage />} />
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
            </main>
            </ErrorBoundary>
          </BrowserRouter>
          <OfflineIndicator />
          <Toaster />
        </div>
        </ProjectProvider>
      </SettingsProvider>
    </AuthContext.Provider>
  );
}

export default App;
